import { useCallback, useEffect, useMemo, useState } from 'react'
import { practitionerRoutes } from '../utils/integrationConfig'
import {
  PractitionerApiError,
  createPractitionerAccount,
  describeError,
  signInPractitioner,
  startPractitionerCheckout,
} from '../utils/practitionerApi'
import type {
  MembershipPlanId,
  PractitionerAccountInput,
  PractitionerSession,
  SignupStep,
} from '../types/practitionerSignup'

function stepFromPath(path: string): SignupStep {
  return path.startsWith(practitionerRoutes.membership) ? 'membership' : 'account'
}

function isSessionValid(session: PractitionerSession | null): session is PractitionerSession {
  // Treat tokens within 60s of expiry as expired.
  return Boolean(session && session.expiresAt - 60_000 > Date.now())
}

/**
 * State for /practitioners/join → /practitioners/membership.
 *
 * Plan state: `selectedPlan` holds the MembershipPlanId the practitioner chose.
 * Session state: the Supabase session lives in React memory only. If it's
 * missing (page refresh, returning from a cancelled Checkout), choosing a plan
 * prompts a sign-in before checkout starts.
 */
export function usePractitionerSignup(initialStep: SignupStep) {
  const [step, setStep] = useState<SignupStep>(initialStep)
  const [session, setSession] = useState<PractitionerSession | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<MembershipPlanId | null>(null)

  const [accountSubmitting, setAccountSubmitting] = useState(false)
  const [accountError, setAccountError] = useState<PractitionerApiError | null>(null)

  const [needsSignIn, setNeedsSignIn] = useState(false)
  const [signInSubmitting, setSignInSubmitting] = useState(false)
  const [signInError, setSignInError] = useState<string | null>(null)

  const [redirectingPlan, setRedirectingPlan] = useState<MembershipPlanId | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  const checkoutCancelled = useMemo(
    () => new URLSearchParams(window.location.search).get('checkout') === 'cancelled',
    [],
  )

  useEffect(() => {
    const onPopState = () => setStep(stepFromPath(window.location.pathname))
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const goTo = useCallback((next: SignupStep) => {
    const path = next === 'membership' ? practitionerRoutes.membership : practitionerRoutes.join
    if (window.location.pathname !== path) window.history.pushState(null, '', path)
    setStep(next)
    window.scrollTo({ top: 0 })
  }, [])

  const beginCheckout = useCallback(
    async (planId: MembershipPlanId, activeSession: PractitionerSession) => {
      setCheckoutError(null)
      setRedirectingPlan(planId)
      try {
        const checkoutUrl = await startPractitionerCheckout(planId, activeSession)
        // Hosted Stripe Checkout collects all payment details.
        window.location.assign(checkoutUrl)
      } catch (error) {
        setCheckoutError(describeError(error))
        setRedirectingPlan(null)
      }
    },
    [],
  )

  const submitAccount = useCallback(
    async (input: PractitionerAccountInput) => {
      setAccountSubmitting(true)
      setAccountError(null)
      try {
        await createPractitionerAccount(input)
      } catch (error) {
        setAccountError(
          error instanceof PractitionerApiError
            ? error
            : new PractitionerApiError('server', describeError(error)),
        )
        setAccountSubmitting(false)
        return
      }

      // Account exists now. Sign in to obtain a session for checkout.
      try {
        const nextSession = await signInPractitioner(input.email, input.password)
        setSession(nextSession)
      } catch (error) {
        // e.g. email confirmation required — ask them to sign in on the next step.
        setNeedsSignIn(true)
        setSignInError(describeError(error))
      }
      setAccountSubmitting(false)
      goTo('membership')
    },
    [goTo],
  )

  const choosePlan = useCallback(
    (planId: MembershipPlanId) => {
      setSelectedPlan(planId)
      setCheckoutError(null)
      if (!isSessionValid(session)) {
        setNeedsSignIn(true)
        return
      }
      void beginCheckout(planId, session)
    },
    [session, beginCheckout],
  )

  const resumeWithSignIn = useCallback(
    async (email: string, password: string) => {
      setSignInSubmitting(true)
      setSignInError(null)
      try {
        const nextSession = await signInPractitioner(email, password)
        setSession(nextSession)
        setNeedsSignIn(false)
        if (selectedPlan) await beginCheckout(selectedPlan, nextSession)
      } catch (error) {
        setSignInError(describeError(error))
      } finally {
        setSignInSubmitting(false)
      }
    },
    [selectedPlan, beginCheckout],
  )

  return {
    step,
    goTo,
    session,
    selectedPlan,
    accountSubmitting,
    accountError,
    submitAccount,
    choosePlan,
    redirectingPlan,
    checkoutError,
    checkoutCancelled,
    needsSignIn,
    signInSubmitting,
    signInError,
    resumeWithSignIn,
    cancelSignIn: () => setNeedsSignIn(false),
  }
}
